import React, {
    CSSProperties,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { PortfolioSummaryConfigurationState } from '../store/instances/configuration/PortfolioSummaryConfigurationState';
import AppContext from 'app/AppContext';
import {
    useSelectCombineSellsAndShorts,
    useShowBuysAsPositive,
} from 'app/store/appSettings/selectors';
import TreemapChart, {
    TreeMapNodeArgs,
} from 'utils/components/TreemapChart/TreemapChart';
import './PortfolioSummaryTreemap.scss';
import { getPortfolioSummaryDataByGroup } from '../aggregationByGroup/portfolioSummaryDataByGroup';
import { toTreemapData } from './portfolioSummaryTreemapData';
import { getAggregationFieldText } from '../aggregation/fields/aggregationFieldDropdownItems';
import { useDispatchUpdateWidgetComponentState } from 'widgets/store/component/dispatcher';
import { getLeafColour } from 'utils/components/TreemapChart/treemapColours';
import classNames from 'classnames';
import { Icon } from '@instinet-analytics/semantic-ui-components';
import {
    PortfolioSummaryComponentState,
    PortfolioSummaryDrillDown,
} from '../store/instances/component/PortfolioSummaryComponentState';
import { useSelectDrillDown } from '../store/instances/component/selectors';
import { useTreemapAggregatedData } from '../treemap/hooks';
import { useDispatchDrillDown } from '../store/data/dispatcher';
import { textUtils } from '@instinet-analytics/core-lib';
import { NumberFormat } from '@instinet-analytics/core-lib';

import styleConstants from '../variables.module.scss';
import treeMapStyleConstants from 'utils/components/TreemapChart/variables.module.scss';
import { mapToReactVisData } from 'utils/components/TreemapChart/treemapData';

const leafBorderSize = parseInt(treeMapStyleConstants.leafBorderSize);

const { pluralise } = textUtils;
export interface PortfolioSummaryTreemapProps {
    config: PortfolioSummaryConfigurationState;
    height: number;
    width: number;
}

const PercentageGroupingThreshold = 5;

export default function PortfolioSummaryTreemap(
    props: PortfolioSummaryTreemapProps
) {
    const { config, height, width } = props;
    const { aggregationField, quantities } = config;

    const { currencyFormat, numberFormat } = useContext(AppContext);
    const showBuysAsPositive = useShowBuysAsPositive();
    const combineSellsAndShorts = useSelectCombineSellsAndShorts();

    const drillDown = useSelectDrillDown();

    const aggregatedData = useTreemapAggregatedData(
        aggregationField,
        drillDown
    );

    const [drilldownColourIndex, setDrilldownColorIndex] = useState(0);

    const dispatchUpdateComponentState =
        useDispatchUpdateWidgetComponentState<PortfolioSummaryComponentState>();
    const dispatchDrillDown = useDispatchDrillDown();

    const drillDownAggregationField = drillDown?.aggregationField;

    /*
     * This is set we have drilled down on the "other" small percentages node
     * This can be done from the top level, or when drilling down on a aggregated value
     */
    const [otherParent, setOtherParent] = useState<TreeMapNodeArgs | undefined>(
        undefined
    );
    const showOtherDrilldown = !!otherParent;

    const treemapData = useMemo(
        () =>
            toTreemapData(
                getPortfolioSummaryDataByGroup(
                    drillDownAggregationField ?? aggregationField,
                    aggregatedData ?? {},
                    {
                        showBuysAsPositive,
                        combineSellsAndShorts,
                        exchangeRate: currencyFormat.exchangeRate ?? 1,
                        addTotalGroup: false,
                    }
                ),
                aggregationField,
                quantities
            ),

        [
            aggregationField,
            currencyFormat.exchangeRate,
            aggregatedData,
            showBuysAsPositive,
            quantities,
            drillDownAggregationField,
            combineSellsAndShorts,
        ]
    );

    const canDrilldown = !drillDown && aggregationField !== 'symbol';

    useEffect(() => {
        // When data changes we'll need to update the "Other" parent if it is set...

        if (otherParent) {
            // Get the node data when we are not drilling down and extract the "Other"
            const reactVisData = mapToReactVisData(
                treemapData,
                PercentageGroupingThreshold,
                false,
                aggregationField === 'side' && canDrilldown
            );

            const nextOther = reactVisData.children?.find(
                (v) => v.title === 'Other'
            );

            setOtherParent(nextOther as TreeMapNodeArgs);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treemapData]);

    const percentageGroupingLabel = getAggregationFieldText(
        drillDown ? drillDown.aggregationField : aggregationField
    );

    const onNodeClicked = (node: TreeMapNodeArgs) => {
        if (!node?.data) {
            return;
        }

        if (node.data.length === 1) {
            if (!canDrilldown) {
                // Clicking on symbol => ignore
                return;
            }

            // Clicking on a node so we can drilldown...
            const nextDrilldownValue = node.data[0].name;

            dispatchDrillDown(aggregationField, {
                aggregationField: 'symbol',
                groupName: nextDrilldownValue,
            });

            setOtherParent(undefined);
        }

        if (node.data.length > 1) {
            // Clicking on an "other" node...
            setOtherParent(node);
        }

        setDrilldownColorIndex(node.colourIndex);
    };

    const drilldownWidth = width - leafBorderSize * 2;
    const drilldownStyle: CSSProperties = { width: drilldownWidth };
    drilldownStyle.backgroundColor = getLeafColour(drilldownColourIndex);
    const drilldownClassNames = classNames(
        'drilldown-header',
        'treemap-node-label',
        'node-colour-' + drilldownColourIndex
    );

    const onCloseDrilldown = () => {
        // Back to the top...
        setOtherParent(undefined);
        dispatchUpdateComponentState({
            drillDown: undefined,
        });
    };

    const showDrilldownPanel = drillDown || otherParent;

    const chartHeight = showDrilldownPanel
        ? height -
          parseInt(styleConstants.drilldownHeaderHeight) -
          parseInt(styleConstants.drilldownHeaderMargin) * 2
        : height;

    return (
        <div className="PortfolioSummaryTreemap">
            {showDrilldownPanel && (
                <div className={drilldownClassNames} style={drilldownStyle}>
                    <DrillDownLabel
                        numberFormat={numberFormat}
                        percentageGroupingLabel={percentageGroupingLabel}
                        drillDown={drillDown}
                        otherParent={otherParent}
                    />

                    <div
                        className="close-button"
                        onClick={() => onCloseDrilldown()}
                    >
                        <Icon name="close" />
                    </div>
                </div>
            )}
            <div style={{ height: chartHeight, width }}>
                <TreemapChart
                    canDrilldown={canDrilldown}
                    showOtherDrilldown={showOtherDrilldown}
                    width={width}
                    height={chartHeight}
                    data={treemapData}
                    percentageGroupingLabel={percentageGroupingLabel}
                    percentGroupingThreshold={PercentageGroupingThreshold}
                    onNodeClicked={onNodeClicked}
                    isSideSummary={aggregationField === 'side'}
                />
            </div>
        </div>
    );
}

function DrillDownLabel(props: {
    numberFormat: NumberFormat;
    percentageGroupingLabel: string;
    drillDown?: PortfolioSummaryDrillDown;
    otherParent?: TreeMapNodeArgs;
}) {
    const { drillDown, otherParent, percentageGroupingLabel, numberFormat } =
        props;

    let otherLabel = '';
    if (otherParent) {
        const percentLabel = `${numberFormat.n0(otherParent.percent)}%`;

        otherLabel = `Other ${otherParent.data.length} ${pluralise(
            otherParent.data.length,
            percentageGroupingLabel
        )} ${percentLabel}`;
    }

    const label = `${drillDown?.groupName ?? ''} ${otherLabel}`.trim();

    return <span>{label}</span>;
}
